import React, { useEffect, useState, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTimeIT } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SafeFormDialog } from "@/components/SafeFormDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Settings2, Plus, Pencil, Trash2, Pause, Play, Lock, Users as UsersIcon, KeyRound, Copy, HardDrive, Database, Upload, AlertTriangle, RotateCcw, Search, X, BellRing, BellOff } from "lucide-react";
import { SuiteCard, SuiteButton, SuiteEyebrow } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import AccessManagement from "@/pages/AccessManagement";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import CausesCatalog from "@/components/settings/CausesCatalog";
import { useEffectiveRole } from "@/foundation/context";
import {
  isDesktopNotificationSupported, getDesktopPermission, getDesktopEnabled,
  setDesktopEnabled, requestDesktopPermission, fireDesktopNotification,
  NOTIFICATION_CATEGORIES, getCategoryPreferences, setCategoryPreference,
} from "@/lib/desktopNotifications";
import {
  isWebPushSupported, enableWebPush, disableWebPush, getWebPushStatus,
  updateWebPushCategories,
} from "@/lib/webPush";
import BackupCenter from "./impostazioni/BackupCenter";
import { DurationInput, DurationFormatBadge } from "@/foundation/duration";

function normalizeCode(raw) {
  if (!raw) return "";
  // rimuove accenti/diacritici mantenendo la lettera base (À → A, È → E)
  const noAccents = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return noAccents
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");
}

const CATEGORY_META = {
  lavoro:                  { icon: "🟢", label: "Lavoro",                    text: "text-emerald-800", dot: "bg-emerald-500", order: 1 },
  assenze_retribuite:      { icon: "🟡", label: "Assenze retribuite",        text: "text-amber-800",   dot: "bg-amber-500",   order: 2 },
  assenze_giustificate:    { icon: "🟠", label: "Assenze giustificate",      text: "text-orange-800",  dot: "bg-orange-500",  order: 3 },
  assenze_non_retribuite:  { icon: "🔴", label: "Assenze non retribuite",    text: "text-red-800",     dot: "bg-red-500",     order: 4 },
  congedi:                 { icon: "🔵", label: "Congedi",                   text: "text-sky-800",     dot: "bg-sky-500",     order: 5 },
  eventi_speciali:         { icon: "🟣", label: "Eventi speciali",           text: "text-violet-800",  dot: "bg-violet-500",  order: 6 },
  // fallback per record legacy
  worked:      { icon: "🟢", label: "Lavoro (legacy)",        text: "text-emerald-800", dot: "bg-emerald-500", order: 1 },
  justified:   { icon: "🟡", label: "Giustificata (legacy)",  text: "text-amber-800",   dot: "bg-amber-500",   order: 2 },
  unjustified: { icon: "🔴", label: "Non giustificata (legacy)", text: "text-red-800",  dot: "bg-red-500",     order: 4 },
};

const BEHAVIOR_FIELDS = [
  { key: "counts_as_work",       label: "Conta come lavoro effettivo",   hint: "Ore incluse nel totale lavorato (es. Ordinario, Straordinario, Recupero)" },
  { key: "reduces_theoretical",  label: "Riduce il teorico giornaliero", hint: "L'assenza copre il profilo (Ferie/Malattia/Festività intere)" },
  { key: "affects_bank",         label: "Incide sulla banca ore",        hint: "Positivo o negativo — modifica il saldo cumulato" },
  { key: "requires_hours",       label: "Richiede numero ore",           hint: "L'utente deve indicare le ore (in Gestione variazioni)" },
  { key: "requires_note",        label: "Richiede nota obbligatoria",    hint: "Non consente il salvataggio senza una nota" },
  { key: "auto_computed",        label: "Calcolo automatico",            hint: "Ore proposte dal sistema (es. Allattamento normativo)" },
  { key: "studio_only",          label: "Solo Studio Paghe",             hint: "Non visibile ai client anche se \"disponibile ai clienti\" è attivo" },
];

const RANGE_DAY_MODE_LABELS = {
  working_days: "Solo giorni lavorativi",
  exclude_sundays: "Tutti i giorni tranne la domenica",
  calendar_days: "Tutti i giorni consecutivi",
};

function effectiveRangeDayMode(cause) {
  const configured = cause?.behavior?.range_day_mode;
  if (configured) return configured;
  if (cause?.code === "CONGEDO_MATRIMONIALE") return "calendar_days";
  if (["MALATTIA", "MATERNITA", "CONG_PARENTALE"].includes(cause?.code)) return "exclude_sundays";
  return "working_days";
}

function normalizeSearch(raw) {
  return String(raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("it-IT")
    .trim();
}

export default function Impostazioni() {
  const { user: currentUser } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [activeCompanyId] = useCompanySelection();
  const normalizedRole = ({
    admin: "super_admin",
    client: "company_admin",
    responsabile: "company_responsible",
  })[currentUser?.role] || currentUser?.role;
  const isSuper = normalizedRole === "super_admin";
  const isCompanyAdmin = normalizedRole === "company_admin";
  const isResponsabile = normalizedRole === "company_responsible";
  const isCompanyRole = isCompanyAdmin || isResponsabile;
  const [tab, setTab] = useState("causes");
  const [causes, setCauses] = useState([]);
  const [causeSearch, setCauseSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [userModal, setUserModal] = useState(false);
  const [userEditing, setUserEditing] = useState(null);
  const [userForm, setUserForm] = useState({ email: "", name: "", role: "operatore", company_id: "", password: "" });
  const [initialUserForm, setInitialUserForm] = useState({ email: "", name: "", role: "operatore", company_id: "", password: "" });
  const userDirty = useMemo(
    () => JSON.stringify(userForm) !== JSON.stringify(initialUserForm),
    [userForm, initialUserForm]
  );
  const [resetInfo, setResetInfo] = useState(null); // {email, new_password}
  const [backups, setBackups] = useState([]);
  const [backupDir, setBackupDir] = useState("");
  const [backupBusy, setBackupBusy] = useState(false);
  // Ripristino da backup
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreConfirmStep, setRestoreConfirmStep] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyCauseForm = {
    code: "", label: "", category: "justified",
    available_to_clients: true, color: "slate", order: 100, notes: "",
  };
  const [form, setForm] = useState(emptyCauseForm);
  const [initialForm, setInitialForm] = useState(emptyCauseForm);
  const causeDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  const [resetLog, setResetLog] = useState([]);
  const load = async () => {
    try {
      const params = isCompanyRole && activeCompanyId && activeCompanyId !== "__all"
        ? { company_id: activeCompanyId }
        : undefined;
      const { data } = await api.get("/causes", { params });
      setCauses(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadUsers = async () => {
    try {
      const promises = [api.get("/users")];
      if (isSuper) promises.push(api.get("/companies"));
      const results = await Promise.all(promises);
      setUsers(results[0].data);
      if (results[1]) setCompanies(sortCompaniesIt(results[1].data));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadResetLog = async () => {
    try {
      const { data } = await api.get("/users/password-reset-log", { params: { limit: 50 } });
      setResetLog(Array.isArray(data) ? data : []);
    } catch (e) { /* silente: fallback su log vuoto */ }
  };
  useEffect(() => {
    load();
    loadUsers();
    loadResetLog();
    /* eslint-disable-next-line */
  }, [activeCompanyId]);

  const loadBackups = async () => {
    try {
      const { data } = await api.get("/admin/backups");
      setBackups(data.backups || []);
      setBackupDir(data.directory || "");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const createBackup = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.post("/admin/backup");
      toast.success(`Backup creato: ${data.filename}`, {
        description: `Percorso: ${data.path} · ${data.size_mb} MB`,
        duration: 8000,
      });
      await loadBackups();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBackupBusy(false); }
  };

  const doRestore = async () => {
    if (!restoreFile) { toast.error("Nessun file selezionato"); return; }
    setRestoreBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/admin/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ripristino completato con successo", {
        description: `Ripristinato da: ${data.restored_from} (${data.restored_size_mb} MB) · Backup pre-ripristino: ${data.pre_restore_backup}`,
        duration: 12000,
      });
      setRestoreFile(null);
      setRestoreConfirmStep(0);
      await loadBackups();
      // Ricarica pagina per rileggere tutti i dati dopo il ripristino
      setTimeout(() => { window.location.reload(); }, 2000);
    } catch (e) {
      toast.error(apiErr(e), { duration: 10000 });
      setRestoreConfirmStep(0);
    } finally { setRestoreBusy(false); }
  };
  useEffect(() => { if (tab === "sistema") loadBackups(); }, [tab]);

  const openNewUser = () => {
    setUserEditing(null);
    const fresh = { email: "", name: "", role: "operatore", company_id: "", password: "" };
    setUserForm(fresh); setInitialUserForm(fresh);
    setUserModal(true);
  };
  const openEditUser = (u) => {
    setUserEditing(u);
    const loaded = {
      email: u.email, name: u.name || "", role: u.role,
      company_id: u.company_id || "", password: "",
    };
    setUserForm(loaded); setInitialUserForm(loaded);
    setUserModal(true);
  };
  const saveUser = async () => {
    try {
      const isScoped = userForm.role === "operatore" || userForm.role === "responsabile";
      if (userEditing) {
        const payload = { email: userForm.email, name: userForm.name, role: userForm.role };
        if (isScoped) payload.company_id = userForm.company_id || null;
        if (userForm.password) payload.password = userForm.password;
        await api.patch(`/users/${userEditing.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        await api.post("/users", {
          email: userForm.email, name: userForm.name, role: userForm.role,
          company_id: isScoped ? userForm.company_id : null,
          password: userForm.password,
        });
        toast.success("Utente creato");
      }
      setUserModal(false);
      setInitialUserForm(userForm);
      await loadUsers();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const resetUserPassword = async (u) => {
    if (!window.confirm(`Generare una nuova password per ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setResetInfo(data);
      loadResetLog();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      await loadUsers();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openNew = () => {
    setEditing(null);
    const fresh = { code: "", label: "", category: "assenze_giustificate",
              available_to_clients: true, color: "slate", order: 100, notes: "",
              behavior: {
                counts_as_work: false, reduces_theoretical: true, affects_bank: false,
                requires_hours: true, requires_note: false, auto_computed: false,
                max_hours_per_day: null, studio_only: false,
                range_day_mode: "working_days",
              },
              codeManuallyEdited: false };
    setForm(fresh); setInitialForm(fresh);
    setModalOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    const loaded = {
      code: c.display_code || c.code, label: c.label, category: c.category,
      available_to_clients: c.available_to_clients, color: c.color || "slate",
      order: c.order, notes: c.notes || "",
      behavior: c.behavior || {
        counts_as_work: false, reduces_theoretical: false, affects_bank: false,
        requires_hours: true, requires_note: false, auto_computed: false,
        max_hours_per_day: null, studio_only: false,
        range_day_mode: effectiveRangeDayMode(c),
      },
      codeManuallyEdited: true,
    };
    loaded.behavior = {
      ...(loaded.behavior || {}),
      range_day_mode: effectiveRangeDayMode(c),
    };
    setForm(loaded); setInitialForm(loaded);
    setModalOpen(true);
  };

  // Auto-generazione del codice dall'etichetta (finché l'utente non lo modifica manualmente)
  const handleLabelChange = (newLabel) => {
    setForm((prev) => {
      const nextCode = prev.codeManuallyEdited ? prev.code : normalizeCode(newLabel);
      return { ...prev, label: newLabel, code: nextCode };
    });
  };
  const handleCodeChange = (newCode) => {
    const norm = normalizeCode(newCode);
    setForm((prev) => ({ ...prev, code: norm, codeManuallyEdited: true }));
  };

  // Preview del conflitto: se il codice corrente esiste già in causes, mostra un warning
  const conflictCause = !editing && form.code
    ? causes.find((c) => (c.display_code || c.code) === form.code)
    : null;

  const canManageCause = (cause) => isSuper || (
    isCompanyAdmin
    && cause?.scope === "company"
    && (!activeCompanyId || activeCompanyId === "__all" || cause.company_id === activeCompanyId)
  );

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/causes/${editing.id}`, {
          label: form.label,
          category: editing.system ? undefined : form.category,
          available_to_clients: form.available_to_clients,
          color: form.color, order: Number(form.order),
          notes: form.notes || null,
          behavior: form.behavior,
        });
        toast.success("Causale aggiornata");
      } else {
        await api.post("/causes", {
          code: form.code || undefined,  // se vuoto, backend lo deriva dall'etichetta
          label: form.label, category: form.category,
          available_to_clients: form.available_to_clients,
          color: form.color, order: Number(form.order),
          notes: form.notes || null,
          behavior: form.behavior,
          company_id: isCompanyAdmin && activeCompanyId !== "__all" ? activeCompanyId : undefined,
        });
        toast.success("Causale creata");
      }
      setInitialForm(form);
      setModalOpen(false);
      await load();
    } catch (e) {
      // Gestione arricchita del conflitto codice
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.error === "code_exists") {
        const openExisting = window.confirm(
          `${det.message}\n\nVuoi aprire la causale esistente per modificarla?`
        );
        if (openExisting) {
          // Ricarico le causali e apro quella esistente in edit
          const params = isCompanyRole && activeCompanyId && activeCompanyId !== "__all"
            ? { company_id: activeCompanyId }
            : undefined;
          const { data } = await api.get("/causes", { params });
          setCauses(data);
          const existing = data.find((c) => c.id === det.existing_id);
          if (existing) {
            openEdit(existing);
            return;
          }
        }
        return;
      }
      toast.error(apiErr(e));
    }
  };
  const toggleActive = async (c) => {
    try {
      await api.patch(`/causes/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Causale sospesa" : "Causale riattivata");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleClient = async (c) => {
    try {
      await api.patch(`/causes/${c.id}`, { available_to_clients: !c.available_to_clients });
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (c) => {
    if (c.system) { toast.error("Le causali di sistema non si eliminano, sospendile."); return; }
    if (!window.confirm(`Eliminare la causale "${c.label}"?`)) return;
    try {
      await api.delete(`/causes/${c.id}`);
      toast.success("Causale eliminata");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const promoteGlobal = async (cause) => {
    if (!window.confirm(
      `Promuovere "${cause.label}" a causale globale?\n\nSara visibile a tutte le aziende.`
    )) return;
    try {
      await api.post(`/causes/${cause.id}/promote-global`);
      toast.success("Causale promossa a livello globale");
      await load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.error === "promotion_conflict") {
        toast.error("Promozione sospesa: causali equivalenti da verificare", {
          description: `${detail.variants?.length || 0} variante/i presenti. Nessuna unione automatica eseguita.`,
          duration: 9000,
        });
        return;
      }
      toast.error(apiErr(error));
    }
  };

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div>
        <SuiteEyebrow icon={Settings2}>Impostazioni</SuiteEyebrow>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>
          {isCompanyRole ? "Impostazioni azienda" : "Configurazione sistema"}
        </h1>
        <p className="text-sm text-suite-neutral-500 mt-1">
          {isCompanyRole
            ? "Causali e accessi circoscritti alla tua azienda. Le causali globali restano disponibili in sola lettura."
            : "Controllo generale del gestionale, delle causali globali e delle configurazioni comuni."}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-suite-neutral-200">
        <button
          onClick={() => setTab("causes")}
          data-testid="tab-causes"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "causes" ? "border-suite-primary text-suite-primary" : "border-transparent text-suite-neutral-500 hover:text-suite-neutral-700"
          }`}
        >
          Causali assenza
        </button>
        <button
          onClick={() => setTab("users")}
          data-testid="tab-users"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "users" ? "border-suite-primary text-suite-primary" : "border-transparent text-suite-neutral-500 hover:text-suite-neutral-700"
          }`}
        >
          Utenti e accessi
        </button>
        <button
          onClick={() => setTab("notifiche")}
          data-testid="tab-notifiche"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "notifiche" ? "border-suite-primary text-suite-primary" : "border-transparent text-suite-neutral-500 hover:text-suite-neutral-700"
          }`}
        >
          Notifiche
        </button>
        {isSuper && (
          <button
            onClick={() => setTab("sicurezza")}
            data-testid="tab-sicurezza"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "sicurezza" ? "border-suite-primary text-suite-primary" : "border-transparent text-suite-neutral-500 hover:text-suite-neutral-700"
            }`}
          >
            Sicurezza
          </button>
        )}
        {isSuper && (
          <button
            onClick={() => setTab("sistema")}
            data-testid="tab-sistema"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "sistema" ? "border-suite-primary text-suite-primary" : "border-transparent text-suite-neutral-500 hover:text-suite-neutral-700"
            }`}
          >
            Sistema
          </button>
        )}
      </div>

      {tab === "sicurezza" && isSuper && (<SicurezzaSection />)}
      {tab === "notifiche" && (<NotificheDesktopSection />)}

      {tab === "causes" && (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">
            {isSuper
              ? "Le causali globali sono disponibili per tutte le aziende. Le causali aziendali possono essere promosse solo dopo un controllo dei duplicati."
              : isCompanyAdmin
                ? "Puoi creare e gestire causali riservate alla tua azienda. Le causali globali sono disponibili in sola lettura."
                : "Puoi consultare le causali globali e quelle configurate per la tua azienda."}
          </div>

          <CausesCatalog
            causes={causes}
            canManageCause={canManageCause}
            isSuper={isSuper}
            isCompanyAdmin={isCompanyAdmin}
            openNew={openNew}
            openEdit={openEdit}
            toggleActive={toggleActive}
            toggleClient={toggleClient}
            remove={remove}
            promoteGlobal={promoteGlobal}
            effectiveRangeDayMode={effectiveRangeDayMode}
            rangeDayModeLabels={RANGE_DAY_MODE_LABELS}
          />
        </div>
      )}

      {tab === "users" && (
        <AccessManagement />
      )}
      {tab === "users_legacy" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-suite-neutral-600">
              {isResponsabile ? (
                <>Reset password degli utenti della tua azienda. Comunica la password temporanea via canale sicuro.</>
              ) : (
                <>Gestione degli accessi al portale. <b>Amministratore</b> = accesso completo. <b>Responsabile</b> = una azienda con reset password. <b>Operatore</b> = solo una azienda.</>
              )}
            </div>
            {!isResponsabile && (
              <SuiteButton onClick={openNewUser} icon={Plus} data-testid="new-user-btn">
                Nuovo utente
              </SuiteButton>
            )}
          </div>

          <SuiteCard padding="none" testid="users-list-card">
              <div className="p-3 bg-slate-50 grid grid-cols-12 gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <div className="col-span-3">Email · Nome</div>
                <div className="col-span-2">Ruolo</div>
                <div className="col-span-4">Azienda</div>
                <div className="col-span-3 text-right">Azioni</div>
              </div>
              {users.map((u) => (
                <div key={u.id} className="p-3 grid grid-cols-12 gap-2 items-center text-sm border-t border-slate-100 hover:bg-slate-50/50"
                     data-testid={`user-row-${u.id}`}>
                  <div className="col-span-3 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{u.email}</div>
                    <div className="text-xs text-slate-500 truncate">{u.name || "—"}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    {u.role === "amministratore" ? (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">Amministratore</span>
                    ) : u.role === "responsabile" ? (
                      <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200 font-semibold">Responsabile</span>
                    ) : u.role === "operatore" ? (
                      <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200 font-semibold">Operatore</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{u.role}</span>
                    )}
                  </div>
                  <div className="col-span-4 text-xs text-slate-600 truncate">{u.company_name || "—"}</div>
                  <div className="col-span-3 flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => resetUserPassword(u)}
                            title="Genera nuova password"
                            className="text-slate-600" data-testid={`reset-pw-${u.id}`}>
                      <KeyRound size={14} />
                    </Button>
                    {!isResponsabile && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeUser(u)}
                                className="text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {users.length === 0 && (
                <div className="p-8 text-center text-suite-neutral-400 text-sm">Nessun utente</div>
              )}
          </SuiteCard>

          {/* Cronologia reset password (audit) */}
          <SuiteCard padding="none" testid="password-reset-log-card">
              <div className="p-3 bg-suite-neutral-50 border-b border-suite-neutral-200 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-suite-neutral-800 flex items-center gap-2">
                    <KeyRound size={14} /> Cronologia reset password
                  </div>
                  <div className="text-[11px] text-suite-neutral-500 mt-0.5">
                    Ultimi 50 reset · chi ha rigenerato la password di chi e quando
                  </div>
                </div>
                <SuiteButton size="sm" variant="ghost" onClick={loadResetLog} icon={RotateCcw} data-testid="refresh-reset-log-btn">
                  Aggiorna
                </SuiteButton>
              </div>
              <div className="p-3 bg-slate-50/50 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-b border-slate-100">
                <div className="col-span-3">Quando</div>
                <div className="col-span-3">Chi ha resettato</div>
                <div className="col-span-3">Utente resettato</div>
                <div className="col-span-2">Tipo</div>
                <div className="col-span-1 text-right">Email</div>
              </div>
              {resetLog.length === 0 ? (
                <div className="p-6 text-center text-suite-neutral-400 text-sm">Nessun reset registrato</div>
              ) : (
                resetLog.map((row) => (
                  <div key={row.id} className="p-3 grid grid-cols-12 gap-2 items-center text-[13px] border-t border-slate-100 hover:bg-slate-50/40" data-testid={`reset-log-row-${row.id}`}>
                    <div className="col-span-3 text-slate-700 font-mono text-[11px]">
                      {row.created_at ? formatDateTimeIT(row.created_at) : "—"}
                    </div>
                    <div className="col-span-3 min-w-0">
                      <div className="text-slate-800 truncate">{row.actor_email || "—"}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{row.actor_role}</div>
                    </div>
                    <div className="col-span-3 min-w-0">
                      <div className="text-slate-800 truncate">{row.target_email}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{row.target_role}</div>
                    </div>
                    <div className="col-span-2 text-[11px]">
                      {row.kind === "admin_reset" ? (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">Admin</span>
                      ) : row.kind === "responsabile_reset" ? (
                        <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200">Responsabile</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">Self (email)</span>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      {row.email_sent ? (
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="Email inviata" />
                      ) : (
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400" title={row.email_error || "Email non inviata"} />
                      )}
                    </div>
                  </div>
                ))
              )}
          </SuiteCard>
        </div>
      )}

      {tab === "sistema" && (
        <div className="space-y-4" data-testid="sistema-tab-content">
          <BackupCenter />
        </div>
      )}

      {/* Modal nuovo/modifica utente */}
      <SafeFormDialog
        open={userModal}
        onOpenChange={setUserModal}
        isDirty={userDirty}
        size="md"
        title={userEditing ? "Modifica utente" : "Nuovo utente"}
        description="Le modifiche saranno salvate solo cliccando su Salva."
      >
        <form onSubmit={(e) => { e.preventDefault(); saveUser(); }} className="space-y-3">
            <div>
              <Label>Nome completo</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                     placeholder="es. Maria Rossi" data-testid="user-form-name" />
            </div>
            <div>
              <Label>Email di accesso</Label>
              <Input type="email" value={userForm.email}
                     onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                     placeholder="nome@azienda.it" data-testid="user-form-email" />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="amministratore">Amministratore (accesso completo)</SelectItem>
                  <SelectItem value="responsabile">Responsabile (una azienda · può resettare password)</SelectItem>
                  <SelectItem value="operatore">Operatore (una sola azienda)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(userForm.role === "operatore" || userForm.role === "responsabile") && (
              <div>
                <Label>Azienda collegata</Label>
                <Select value={userForm.company_id || ""} onValueChange={(v) => setUserForm({ ...userForm, company_id: v })}>
                  <SelectTrigger data-testid="user-form-company"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{userEditing ? "Nuova password (lascia vuoto per mantenere)" : "Password iniziale"}</Label>
              <Input type="text" value={userForm.password}
                     onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                     placeholder={userEditing ? "•••••• (non modificata)" : "min 6 caratteri"}
                     className="font-mono" data-testid="user-form-password" />
              <div className="text-[11px] text-slate-500 mt-1">
                Comunica la password all&apos;utente. Potrà cambiarla dal proprio profilo (funzione futura).
              </div>
            </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={() => setUserModal(false)} data-testid="cancel-user-btn">Annulla</Button>
            <Button type="submit" className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="save-user-btn">
              {userEditing ? "Salva" : "Crea utente"}
            </Button>
          </div>
        </form>
      </SafeFormDialog>

      {/* Modal risultato reset password */}
      <Dialog open={!!resetInfo} onOpenChange={(v) => !v && setResetInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Password rigenerata</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {resetInfo?.email_sent ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="reset-email-sent-banner">
                <div className="font-semibold mb-1">Email inviata correttamente</div>
                <div className="text-[13px]">
                  La password temporanea è stata spedita via email a <b>{resetInfo?.email}</b>. Non serve annotarla: la trova nella sua casella.
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="reset-email-fallback-banner">
                <div className="font-semibold mb-1">Email non spedita</div>
                <div className="text-[12px]">
                  {resetInfo?.email_error === "not_configured"
                    ? "Il servizio email non è configurato. Comunica la password all'utente a voce (visibile qui sotto una sola volta)."
                    : `Errore invio email (${resetInfo?.email_error || "sconosciuto"}). Comunica la password a voce.`}
                </div>
              </div>
            )}
            <div className="text-sm text-slate-600">
              Nuova password per <b>{resetInfo?.email}</b>:
            </div>
            <div className="flex items-center gap-2 p-3 bg-slate-100 rounded border border-slate-200 font-mono text-sm">
              <span className="flex-1 select-all" data-testid="new-password-value">{resetInfo?.new_password}</span>
              <Button size="sm" variant="ghost"
                      onClick={() => { navigator.clipboard.writeText(resetInfo?.new_password || ""); toast.success("Copiata"); }}>
                <Copy size={14} />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetInfo(null)} className="bg-emerald-800 hover:bg-emerald-700 text-white">
              Ho annotato la password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Modal nuova/modifica causale */}
      <SafeFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        isDirty={causeDirty}
        size="md"
        title={editing ? "Modifica causale" : "Nuova causale"}
        description="Le modifiche saranno salvate solo cliccando su Salva."
      >
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-3">
            <div>
              <Label>Etichetta <span className="text-red-600">*</span></Label>
              <Input value={form.label}
                     onChange={(e) => handleLabelChange(e.target.value)}
                     placeholder="es. Maternità, Permesso visite mediche…"
                     data-testid="form-cause-label" />
              <div className="text-[11px] text-slate-500 mt-1">
                Nome visualizzato in tutte le maschere e nei report.
              </div>
            </div>
            <div>
              <Label>
                Codice tecnico
                <span className="text-slate-400 font-normal ml-1">(generato automaticamente)</span>
              </Label>
              <Input value={form.code}
                     onChange={(e) => handleCodeChange(e.target.value)}
                     disabled={!!editing}
                     placeholder="MATERNITA"
                     className="font-mono"
                     data-testid="form-cause-code" />
              <div className="text-[11px] text-slate-500 mt-1">
                Solo A-Z, 0-9 e underscore. Modificalo solo se necessario.
              </div>
              {conflictCause && (
                <div className="mt-2 p-2 rounded-md border border-amber-300 bg-amber-50 text-[12px] text-amber-900" data-testid="conflict-warning">
                  <div className="font-semibold mb-1">
                    Esiste già una causale con questo codice
                  </div>
                  <div>
                    <span className="font-mono">{conflictCause.code}</span> · {conflictCause.label}
                    {" "}({conflictCause.active ? "attiva" : "sospesa"},
                    {" "}{conflictCause.available_to_clients ? "visibile ai clienti" : "nascosta ai clienti"})
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className="px-2 py-1 rounded bg-amber-800 text-white text-[11px] font-semibold hover:bg-amber-900"
                      onClick={() => { setModalOpen(false); setTimeout(() => openEdit(conflictCause), 100); }}
                      data-testid="conflict-open-existing"
                    >
                      Apri causale esistente
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-amber-300 text-amber-900 text-[11px] font-semibold hover:bg-amber-100"
                      onClick={() => handleCodeChange(form.code + "_2")}
                    >
                      Usa &quot;{form.code}_2&quot;
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}
                      disabled={!!editing?.system}>
                <SelectTrigger data-testid="form-cause-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lavoro">🟢 Lavoro</SelectItem>
                  <SelectItem value="assenze_retribuite">🟡 Assenze retribuite</SelectItem>
                  <SelectItem value="assenze_giustificate">🟠 Assenze giustificate</SelectItem>
                  <SelectItem value="assenze_non_retribuite">🔴 Assenze non retribuite</SelectItem>
                  <SelectItem value="congedi">🔵 Congedi</SelectItem>
                  <SelectItem value="eventi_speciali">🟣 Eventi speciali</SelectItem>
                </SelectContent>
              </Select>
              {editing?.system && <div className="text-[11px] text-slate-500 mt-1">Categoria bloccata (causale di sistema).</div>}
            </div>

            {/* Comportamento */}
            <div className="border-t border-slate-200 pt-3 mt-1">
              <Label className="mb-2 block text-sm font-semibold">Comportamento</Label>
              <div className="space-y-2">
                {BEHAVIOR_FIELDS.map((f) => (
                  <label key={f.key} className="flex items-start gap-2 cursor-pointer" data-testid={`behavior-${f.key}`}>
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-emerald-700"
                      checked={!!form.behavior?.[f.key]}
                      onChange={(e) => setForm({
                        ...form,
                        behavior: { ...form.behavior, [f.key]: e.target.checked },
                      })}
                    />
                    <span className="text-[12px] text-slate-700 leading-tight">
                      <span className="font-medium">{f.label}</span>
                      <span className="block text-[11px] text-slate-500">{f.hint}</span>
                    </span>
                  </label>
                ))}
                <div className="pt-1">
                  <Label className="text-[12px] mb-1 block">Giorni inclusi negli inserimenti Dal–Al</Label>
                  <Select
                    value={form.behavior?.range_day_mode || "working_days"}
                    onValueChange={(value) => setForm({
                      ...form,
                      behavior: { ...form.behavior, range_day_mode: value },
                    })}
                  >
                    <SelectTrigger className="h-9 text-xs" data-testid="behavior-range-day-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="working_days">Solo giorni lavorativi</SelectItem>
                      <SelectItem value="exclude_sundays">Tutti i giorni tranne la domenica</SelectItem>
                      <SelectItem value="calendar_days">Tutti i giorni consecutivi</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-slate-500 mt-1">
                    I riposi inclusi vengono registrati a zero ore: rendono continuo il periodo senza alterare i saldi.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-[12px] w-40 mb-0">Massimo ore giornaliere</Label>
                  <DurationInput
                    placeholder="illimitato"
                    className="w-28"
                    value={form.behavior?.max_hours_per_day ?? ""}
                    onCommit={(v) => setForm((f) => ({ ...f, behavior: { ...f.behavior, max_hours_per_day: v } }))}
                    data-testid="behavior-max-hours"
                  />
                  <DurationFormatBadge data-testid="behavior-max-hours-format-badge" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Colore</Label>
                <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["slate", "emerald", "teal", "amber", "sky", "violet", "rose", "red"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ordine</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.available_to_clients}
                      onCheckedChange={(v) => setForm({ ...form, available_to_clients: v })}
                      data-testid="form-cause-client-toggle" />
              <Label className="mb-0">Visibile ai clienti</Label>
            </div>
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)} data-testid="cancel-cause-btn">Annulla</Button>
            <Button type="submit"
                    disabled={!form.label?.trim() || !form.code || (!editing && !!conflictCause)}
                    className="bg-emerald-800 hover:bg-emerald-700 text-white disabled:opacity-50" data-testid="save-cause-btn">
              {editing ? "Salva" : "Crea"}
            </Button>
          </div>
        </form>
      </SafeFormDialog>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// D2-A · Sezione Sicurezza — Disconnessione automatica per inattività
// ═════════════════════════════════════════════════════════════════════════════
function SicurezzaSection() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || "").toLowerCase() === "admin" || (user?.role || "").toLowerCase() === "super_admin";
  const [cfg, setCfg] = useState(null);      // {enabled, minutes, quick_values, can_edit, min, max}
  const [enabled, setEnabled] = useState(false);
  const [minutes, setMinutes] = useState(60);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState("");  // v1.3.6+ · P0.1 · errore di caricamento separato
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoadErr("");
      try {
        const { data } = await api.get("/settings/security/idle-timeout");
        if (cancel) return;
        setCfg(data);
        setEnabled(!!data.enabled);
        setMinutes(Number(data.minutes) || 60);
      } catch (e) {
        if (!cancel) setLoadErr(apiErr(e));
      }
    })();
    return () => { cancel = true; };
  }, [loadAttempt]);

  const canEdit = isSuperAdmin && cfg?.can_edit;
  const quick = cfg?.quick_values || [15, 30, 60, 120, 240];
  const min = cfg?.min || 5, max = cfg?.max || 480;

  const submit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    setErr("");
    const v = Number(minutes);
    if (!Number.isFinite(v) || v < min || v > max) {
      setErr(`La durata deve essere tra ${min} e ${max} minuti`); return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/security/idle-timeout",
        { enabled: !!enabled, minutes: v });
      setCfg(data);
      toast.success("Impostazione salvata");
    } catch (ex) { setErr(apiErr(ex)); }
    finally { setSaving(false); }
  };

  if (loadErr) {
    // v1.3.6+ · P0.1 · fallback leggibile al posto di schermata vuota
    return (
      <div className="p-6 text-sm text-suite-neutral-700" data-testid="security-load-error">
        <div className="rounded-lg border border-suite-error/40 bg-red-50 p-4 space-y-3">
          <div className="font-semibold text-suite-error">Impossibile caricare le impostazioni di sicurezza</div>
          <div className="text-xs text-suite-neutral-600 break-all">{loadErr}</div>
          <button
            type="button"
            className="px-3 py-1.5 text-xs rounded-md bg-emerald-800 hover:bg-emerald-700 text-white"
            onClick={() => setLoadAttempt((n) => n + 1)}
            data-testid="security-retry-btn"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }
  if (!cfg) return <div className="p-4 text-sm text-suite-neutral-500" data-testid="security-loading">Caricamento…</div>;

  return (
    <div className="space-y-4" data-testid="security-settings-section">
      <SuiteCard padding="lg">
        <div className="mb-4">
          <SuiteEyebrow>Sicurezza · Sessioni</SuiteEyebrow>
          <h2 className="text-lg font-semibold text-suite-neutral-900 mt-1">
            Disconnessione automatica per inattività
          </h2>
          <p className="text-sm text-suite-neutral-600 mt-1">
            Dopo questo periodo senza attività l&apos;utente verrà disconnesso
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 max-w-2xl">
          <div className="flex items-center gap-3">
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={!canEdit}
              data-testid="idle-timeout-enabled-switch"
            />
            <Label htmlFor="idle-timeout-enabled-switch"
                   className="text-sm font-medium text-suite-neutral-800">
              {enabled ? "Attivato" : "Disattivato"}
            </Label>
          </div>

          <div>
            <Label className="text-sm font-medium text-suite-neutral-800 block mb-2">
              Durata (minuti)
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {quick.map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={!canEdit || !enabled}
                  onClick={() => setMinutes(v)}
                  data-testid={`idle-timeout-quick-${v}`}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors
                    ${minutes === v
                      ? "bg-suite-primary text-white border-suite-primary"
                      : "bg-white text-suite-neutral-800 border-suite-neutral-300 hover:border-suite-primary"}
                    disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {v} min
                </button>
              ))}
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min={min}
                  max={max}
                  value={minutes}
                  onChange={(e) => setMinutes(Number(e.target.value))}
                  disabled={!canEdit || !enabled}
                  className="w-24 h-9"
                  data-testid="idle-timeout-minutes-input"
                />
                <span className="text-xs text-suite-neutral-500">min</span>
              </div>
            </div>
            <p className="text-xs text-suite-neutral-500 mt-2">
              Personalizzato: da {min} a {max} minuti
            </p>
          </div>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
                 data-testid="idle-timeout-error">
              {err}
            </div>
          )}

          {!canEdit && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2"
               data-testid="idle-timeout-readonly-notice">
              Solo il super amministratore può modificare questa impostazione.
              Il valore attuale è visibile in sola lettura.
            </p>
          )}

          {canEdit && (
            <div className="flex justify-end">
              <SuiteButton type="submit" disabled={saving}
                            data-testid="idle-timeout-save-btn">
                {saving ? "Salvataggio…" : "Salva"}
              </SuiteButton>
            </div>
          )}
        </form>
      </SuiteCard>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// C5 · Sezione Notifiche Desktop — popup nativi del browser
// ═════════════════════════════════════════════════════════════════════════════
function NotificheDesktopSection() {
  const [supported] = useState(isDesktopNotificationSupported());
  const [permission, setPermission] = useState(getDesktopPermission());
  const [enabled, setEnabled] = useState(getDesktopEnabled());
  const [categories, setCategories] = useState(getCategoryPreferences());
  const [busy, setBusy] = useState(false);

  // Web Push state
  const [webPushSupported] = useState(isWebPushSupported());
  const [webPushStatus, setWebPushStatus] = useState({ subscribed: false, permission: "default" });
  const [webPushBusy, setWebPushBusy] = useState(false);

  const refresh = () => {
    setPermission(getDesktopPermission());
    setEnabled(getDesktopEnabled());
    setCategories(getCategoryPreferences());
  };

  const refreshWebPush = async () => {
    if (!webPushSupported) return;
    try { setWebPushStatus(await getWebPushStatus()); }
    catch (_e) { /* ignore */ }
  };
  useEffect(() => { refreshWebPush(); /* eslint-disable-next-line */ }, []);

  const toggleCategory = (key, on) => {
    setCategoryPreference(key, on);
    setCategories(getCategoryPreferences());
    // Sincronizza anche lato server per Web Push
    if (webPushStatus.subscribed) {
      updateWebPushCategories(getCategoryPreferences());
    }
  };

  const handleEnableWebPush = async () => {
    setWebPushBusy(true);
    try {
      await enableWebPush(getCategoryPreferences());
      toast.success("Web Push attivo · riceverai avvisi anche a browser chiuso");
      await refreshWebPush();
    } catch (e) {
      toast.error(e?.message || "Impossibile attivare Web Push");
    } finally { setWebPushBusy(false); }
  };

  const handleDisableWebPush = async () => {
    setWebPushBusy(true);
    try {
      await disableWebPush();
      toast.success("Web Push disattivato");
      await refreshWebPush();
    } finally { setWebPushBusy(false); }
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      const wasAlreadyGranted = getDesktopPermission() === "granted"; // v1.3.6+ P0.2
      const result = await requestDesktopPermission();
      if (result === "granted") {
        setDesktopEnabled(true);
        refresh();
        if (wasAlreadyGranted) {
          // Il permesso era gia' concesso: il browser NON mostra alcun prompt.
          // Senza questo messaggio l'utente percepirebbe "nessun effetto" (P0.2).
          toast.success("Notifiche desktop riattivate (permesso già concesso in precedenza).");
        } else {
          toast.success("Notifiche desktop attivate");
          fireDesktopNotification({
            title: "Gestionale Presenze",
            body: "Le notifiche desktop sono attive. Riceverai qui gli avvisi importanti.",
            tag: "welcome",
          });
        }
      } else if (result === "denied") {
        toast.error("Permesso negato. Autorizza le notifiche dalle impostazioni del browser per questo sito.");
      } else {
        toast.info("Richiesta annullata");
      }
    } finally { setBusy(false); }
  };

  const handleDisable = () => {
    setDesktopEnabled(false);
    refresh();
    toast.success("Notifiche desktop disattivate");
  };

  const testNotify = () => {
    const ok = fireDesktopNotification({
      title: "Prova notifica",
      body: "Se vedi questo popup fuori dal browser, le notifiche desktop funzionano.",
      tag: "test",
    });
    if (!ok) toast.error("Non è stato possibile generare la notifica. Verifica il permesso del browser.");
  };

  return (
    <div className="space-y-4" data-testid="desktop-notifications-section">
      <SuiteCard padding="lg">
        <div className="mb-4">
          <SuiteEyebrow>Notifiche · Desktop</SuiteEyebrow>
          <h2 className="text-lg font-semibold text-suite-neutral-900 mt-1">
            Avvisi push del browser
          </h2>
          <p className="text-sm text-suite-neutral-500 mt-1 max-w-2xl">
            Ricevi un popup nativo dal browser (macOS/Windows/Linux) quando arrivano
            richieste di ferie in attesa o scadenze imminenti D.Lgs. 81/2008.
            Le notifiche funzionano finché la scheda del gestionale è aperta.
          </p>
        </div>

        {!supported ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Il tuo browser non supporta le notifiche desktop. Aggiornalo o utilizza Chrome/Firefox/Edge/Safari.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-suite-neutral-500 text-xs uppercase tracking-widest">Stato attuale:</span>
                {permission === "granted" && enabled && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">
                    <BellRing size={12} /> Attive
                  </span>
                )}
                {permission === "granted" && !enabled && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 text-slate-700 px-2 py-0.5 text-xs font-semibold">
                    <BellOff size={12} /> Autorizzate ma disattivate
                  </span>
                )}
                {permission === "default" && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">
                    Non ancora richieste
                  </span>
                )}
                {permission === "denied" && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-red-100 text-red-700 px-2 py-0.5 text-xs font-semibold">
                    Permesso negato dal browser
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {permission !== "granted" || !enabled ? (
                <SuiteButton
                  onClick={handleEnable}
                  loading={busy}
                  icon={BellRing}
                  disabled={permission === "denied"}
                  data-testid="enable-desktop-notifications"
                >
                  {permission === "granted" ? "Riattiva notifiche" : "Attiva notifiche desktop"}
                </SuiteButton>
              ) : (
                <SuiteButton
                  variant="secondary"
                  onClick={handleDisable}
                  icon={BellOff}
                  data-testid="disable-desktop-notifications"
                >
                  Disattiva notifiche desktop
                </SuiteButton>
              )}
              {permission === "granted" && enabled && (
                <SuiteButton
                  variant="outline"
                  onClick={testNotify}
                  data-testid="test-desktop-notifications"
                >
                  Invia notifica di prova
                </SuiteButton>
              )}
            </div>

            {permission === "denied" && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <div className="font-semibold mb-1">Come riattivare</div>
                Clicca sull&apos;icona del lucchetto/scudo nella barra dell&apos;indirizzo del browser,
                trova &laquo;Notifiche&raquo; e imposta su &laquo;Consenti&raquo;, poi ricarica la pagina.
              </div>
            )}

            {permission === "granted" && enabled && (
              <div className="rounded-lg border border-suite-neutral-200 bg-white p-3" data-testid="desktop-categories">
                <div className="mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-suite-primary">Categorie di notifiche</div>
                  <div className="text-[11.5px] text-suite-neutral-500 mt-0.5">
                    Scegli quali eventi generano un popup desktop. Le notifiche in-app (campanellino) restano sempre attive.
                  </div>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {Object.entries(NOTIFICATION_CATEGORIES).map(([key, cfg]) => {
                    const on = categories[key] !== false;
                    return (
                      <label
                        key={key}
                        htmlFor={`cat-${key}`}
                        className={`flex items-start gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                          on ? "border-suite-primary/40 bg-suite-primary-light/20" : "border-suite-neutral-200 bg-suite-neutral-50/50 hover:bg-suite-neutral-50"
                        }`}
                        data-testid={`cat-row-${key}`}
                      >
                        <Switch
                          id={`cat-${key}`}
                          checked={on}
                          onCheckedChange={(v) => toggleCategory(key, v)}
                          className="mt-0.5"
                          data-testid={`cat-switch-${key}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-suite-neutral-900">{cfg.label}</div>
                          <div className="text-[11px] leading-4 text-suite-neutral-500 mt-0.5">{cfg.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="rounded-md border border-suite-neutral-200 bg-white px-3 py-2 text-xs text-suite-neutral-500 space-y-1">
              <div>• Le notifiche vengono generate ogni 30 secondi in base alle nuove notifiche del gestionale.</div>
              <div>• Cliccando sul popup apri direttamente la notifica nel gestionale.</div>
              <div>• Le notifiche funzionano quando la scheda è aperta (anche in background).</div>
              <div>• Le informazioni personali NON vengono inviate a servizi esterni.</div>
            </div>
          </div>
        )}
      </SuiteCard>

      {/* ============= Web Push (browser chiuso) ============= */}
      <SuiteCard padding="lg" testid="web-push-card">
        <div className="mb-4">
          <SuiteEyebrow>Notifiche · Web Push</SuiteEyebrow>
          <h2 className="text-lg font-semibold text-suite-neutral-900 mt-1">
            Avvisi anche a browser chiuso
          </h2>
          <p className="text-sm text-suite-neutral-500 mt-1 max-w-2xl">
            Registra questo dispositivo per ricevere le notifiche urgenti anche
            quando il gestionale non è aperto. Utilizza il protocollo standard Web
            Push (VAPID) e le stesse preferenze di categoria configurate sopra.
          </p>
        </div>

        {!webPushSupported ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Il tuo browser non supporta Web Push. Utilizza Chrome, Firefox o Edge aggiornati.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-suite-neutral-500 text-xs uppercase tracking-widest">Stato:</span>
                {webPushStatus.subscribed ? (
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">
                    <BellRing size={12} /> Registrato · anche a browser chiuso
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 text-slate-700 px-2 py-0.5 text-xs font-semibold">
                    <BellOff size={12} /> Non registrato
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {webPushStatus.subscribed ? (
                <SuiteButton
                  variant="secondary"
                  onClick={handleDisableWebPush}
                  loading={webPushBusy}
                  icon={BellOff}
                  data-testid="disable-web-push"
                >
                  Disattiva Web Push
                </SuiteButton>
              ) : (
                <SuiteButton
                  onClick={handleEnableWebPush}
                  loading={webPushBusy}
                  icon={BellRing}
                  data-testid="enable-web-push"
                >
                  Attiva Web Push su questo dispositivo
                </SuiteButton>
              )}
            </div>

            <div className="rounded-md border border-suite-neutral-200 bg-white px-3 py-2 text-xs text-suite-neutral-500 space-y-1">
              <div>• Funziona anche quando la scheda o il browser sono chiusi.</div>
              <div>• Va attivato su ogni dispositivo (PC di lavoro, PC di casa, ecc.).</div>
              <div>• Rispetta le preferenze di categoria configurate sopra.</div>
              <div>• I messaggi sono cifrati end-to-end tra il server e il browser.</div>
            </div>
          </div>
        )}
      </SuiteCard>
    </div>
  );
}
